import React from "react";
import { motion } from "framer-motion";

const AssignmentStatusChart = ({
  allAssignmentsCount,
  pendingAssignmentsCount,
  submittedAssignmentsCount: propSubmittedCount,
  returnedAssignmentsCount = 0
}) => {
  const submittedAssignmentsCount = propSubmittedCount !== undefined
    ? propSubmittedCount
    : (allAssignmentsCount - pendingAssignmentsCount);

  const total = allAssignmentsCount || 1; // avoid division by zero

  const segments = [
    {
      label: "Submitted",
      count: submittedAssignmentsCount,
      percent: ((submittedAssignmentsCount / total) * 100).toFixed(0),
      color: "#10B981",
      barClass: "bg-emerald-500",
      countClass: "text-emerald-600 dark:text-emerald-400",
      bgClass: "bg-emerald-100 dark:bg-emerald-900/20",
    },
    {
      label: "Pending",
      count: pendingAssignmentsCount,
      percent: ((pendingAssignmentsCount / total) * 100).toFixed(0),
      color: "#3B82F6",
      barClass: "bg-blue-500",
      countClass: "text-blue-600 dark:text-blue-400",
      bgClass: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      label: "Returned",
      count: returnedAssignmentsCount,
      percent: ((returnedAssignmentsCount / total) * 100).toFixed(0),
      color: "#F97316",
      barClass: "bg-orange-500",
      countClass: "text-orange-600 dark:text-orange-400",
      bgClass: "bg-orange-100 dark:bg-orange-900/20",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Total header */}
      <div className="flex items-baseline gap-3">
        <span className="text-4xl font-black tracking-tight text-gray-900 dark:text-white">
          {allAssignmentsCount}
        </span>
        <span className="text-xs uppercase tracking-widest text-gray-500 font-medium">
          Total Assignments
        </span>
      </div>

      {/* Stacked horizontal bar */}
      <div className="w-full h-3 rounded-sm bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
        {segments.map((seg) => (
          <motion.div
            key={seg.label}
            initial={{ width: 0 }}
            animate={{ width: `${(seg.count / total) * 100}%` }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className={`${seg.barClass} h-full`}
            style={{ minWidth: seg.count > 0 ? "4px" : "0" }}
          />
        ))}
      </div>

      {/* Stat rows */}
      <div className="space-y-2">
        {segments.map((seg, index) => (
          <motion.div
            key={seg.label}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
            className={`flex items-center justify-between p-3 rounded-sm ${seg.bgClass}`}
            style={{ borderLeft: `3px solid ${seg.color}` }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-2 h-5 rounded-sm"
                style={{ backgroundColor: seg.color }}
              />
              <span className="text-xs uppercase tracking-wider font-bold text-gray-700 dark:text-gray-200">
                {seg.label}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-lg font-black ${seg.countClass}`}>
                {seg.count}
              </span>
              <span className="text-xs text-gray-400 font-medium">
                {seg.percent}%
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AssignmentStatusChart;
